<template>
  <div class="container">
    <h1 class="page-title">帕豹</h1>
    <div class="letter-container">
      <transition name="fade" @after-leave="onImageFadeComplete" @after-enter="onImageAppearComplete">
        <div v-if="!isHovered" class="image-container">
          <div class="letter-image-wrapper" 
               @mouseenter="showSongTooltip = true" 
               @mouseleave="showSongTooltip = false"
               @click="showYoutubeVideo">
            <img src="/letter.jpg" alt="Letter" class="letter-image" />
            <div class="song-tooltip" v-if="showSongTooltip">
              點擊觀看生日快樂影片
            </div>
          </div>
          <img src="/signature.png" 
               alt="Signature" 
               class="signature-image" 
               @click="startPasswordPanel" />
        </div>
      </transition>
      
      <transition name="fade-in" @after-leave="onPanelFadeComplete">
        <div v-if="showPanel" class="input-panel">
          <div class="close-btn" @click="closePasswordPanel">×</div>
          <div class="heading-container">
            <h3>請輸入密碼</h3>
            <div 
              class="help-icon" 
              @mouseenter="showTooltip = true" 
              @mouseleave="showTooltip = false"
            >
              ?
              <div class="tooltip" v-if="showTooltip">
                魂丸西元生日年月日8碼（限帕豹本人觀看）
              </div>
            </div>
          </div>
          
          <form @submit.prevent="verifyPassword">
            <div class="password-container">
              <input 
                type="password" 
                class="password-input" 
                v-model="password" 
                placeholder="請輸入密碼"
                :disabled="isVerifying"
              />
            </div>
            
            <div v-if="passwordError" class="error-message">
              {{ passwordError }}
            </div>
            
            <button class="submit-btn" type="submit" :disabled="isVerifying">
              {{ isVerifying ? '驗證中...' : '確認' }}
            </button>
          </form>
        </div>
      </transition>
      
      <!-- YouTube video modal -->
      <div v-if="showVideoModal" class="video-modal" @click="closeVideoModal">
        <div class="video-container" @click.stop>
          <div class="close-video-btn" @click="closeVideoModal">×</div>
          <iframe 
            :src="'https://www.youtube.com/embed/tr0W-NkKp2U?autoplay=1'" 
            frameborder="0" 
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" 
            allowfullscreen>
          </iframe>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'IndexPage',
  head() {
    return {
      title: '給帕豹的一封信',
      link: [
        {
          rel: 'stylesheet',
          href: 'https://fonts.googleapis.com/css2?family=Noto+Sans+SC:wght@400;500;700&family=Noto+Serif+SC:wght@400;500;600;700&family=Ma+Shan+Zheng&family=Playfair+Display:wght@400;500;600;700&display=swap'
        }
      ]
    }
  },
  setup() {
    // Use auth composable for authentication logic
    const auth = useAuth();
    
    // Check if already authenticated
    if (process.client && auth.checkAuthentication()) {
      // If already authenticated, redirect to thank you page
      // Use setTimeout to avoid hydration issues
      setTimeout(() => {
        navigateTo('/thankyou');
      }, 0);
    }
    
    return { auth };
  },
  data() {
    return {
      isHovered: false,
      isAnimationComplete: false,
      showPanel: false,
      password: '',
      showTooltip: false,
      showSongTooltip: false,
      passwordError: '',
      isVerifying: false,
      showVideoModal: false,
    }
  },
  methods: {
    // Only start transition when signature is clicked
    startPasswordPanel() {
      this.isHovered = true;
      this.isAnimationComplete = false;
    },
    // Close password panel when clicking the X button
    closePasswordPanel() {
      this.showPanel = false;
      // isHovered remains true until panel completely fades
    },
    onImageFadeComplete() {
      // Called when image has faded out
      if (this.isHovered) {
        this.showPanel = true;
      }
    },
    onPanelFadeComplete() {
      // Only change hover state after panel is completely gone
      if (!this.showPanel) {
        this.isHovered = false;
      }
    },
    onImageAppearComplete() {
      // Reset animation states when image fully appears
      this.isAnimationComplete = false;
    },
    // Replace playBirthdaySong with showYoutubeVideo
    showYoutubeVideo() {
      if (!process.client) return;
      this.showVideoModal = true;
    },
    closeVideoModal() {
      this.showVideoModal = false;
    },
    async verifyPassword() {
      // Only proceed if running in client
      if (!process.client) return;
      
      // Prevent multiple submissions
      if (this.isVerifying) return;
      
      // Show loading state
      this.isVerifying = true;
      this.passwordError = "";
      
      try {
        // Use the auth composable to verify the password
        const response = await this.auth.verifyPassword(this.password);
        
        if (response.success) {
          // Create auth token and navigate to thank you page
          this.auth.createAuthToken();
          navigateTo('/thankyou');
        } else {
          this.passwordError = "密碼錯誤，請重新輸入";
        }
      } catch (error) {
        console.error('Error verifying password:', error);
        this.passwordError = "驗證錯誤，請重新整理頁面";
      } finally {
        this.isVerifying = false;
      }
    }
  }
}
</script>

<style scoped>
.container {
  min-height: 100vh;
  width: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background: linear-gradient(135deg, #f8f0e3 0%, #d4b48e 100%);
  padding: 20px;
  box-sizing: border-box;
  position: relative;
  overflow: hidden;
}

.page-title {
  color: #8B4F13;
  font-size: 3rem;
  margin-bottom: 1.5rem;
  text-align: center;
  font-weight: 700;
  letter-spacing: 1px;
  text-shadow: 2px 2px 3px rgba(184, 134, 50, 0.2);
  position: relative;
  z-index: 3;
  font-family: 'Noto Serif SC', 'Ma Shan Zheng', sans-serif;
}

.page-title::after {
  content: '';
  display: block;
  width: 100px;
  height: 3px;
  background: linear-gradient(90deg, transparent, rgba(184, 134, 50, 0.6), transparent);
  margin: 10px auto 0;
}

.container::before {
  content: '';
  position: absolute;
  top: -10%;
  left: -10%;
  width: 120%;
  height: 120%;
  background-image: radial-gradient(circle, rgba(184, 134, 50, 0.2) 2px, transparent 3px);
  background-size: 30px 30px;
  pointer-events: none;
  z-index: 1;
}

.letter-container {
  background-color: #fff9f0;
  border-radius: 12px;
  box-shadow: 0 15px 35px rgba(139, 79, 19, 0.15), 0 5px 15px rgba(0, 0, 0, 0.07);
  padding: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  position: relative;
  border: 1px solid rgba(184, 134, 50, 0.2);
  z-index: 2;
  overflow: hidden;
  min-width: 350px;
  min-height: 400px;
  width: 90%;
  max-width: 600px;
}

.image-container, .input-panel {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  position: relative;
  min-height: 400px;
}

.letter-image-wrapper {
  position: relative;
  cursor: pointer;
  transition: transform 0.4s ease, filter 0.4s ease;
}

.letter-image-wrapper:hover {
  transform: scale(1.03);
  filter: drop-shadow(0 10px 15px rgba(139, 79, 19, 0.2));
}

.letter-image {
  display: block;
  max-width: 100%;
  height: auto;
  object-fit: contain;
  box-shadow: 0 8px 20px rgba(139, 79, 19, 0.1);
}

.signature-image {
  position: absolute;
  bottom: 20px;
  right: 20px;
  max-width: 150px;
  height: auto;
  filter: drop-shadow(0 2px 4px rgba(139, 79, 19, 0.15));
  transition: transform 0.4s ease, filter 0.4s ease;
  z-index: 3;
  cursor: pointer;
}

.signature-image:hover {
  transform: scale(1.08);
  filter: drop-shadow(0 8px 12px rgba(139, 79, 19, 0.3));
}

/* Song tooltip styles */
.song-tooltip {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  background-color: rgba(255, 249, 240, 0.9);
  border: 1px solid rgba(139, 79, 19, 0.3);
  border-radius: 6px;
  padding: 6px 12px;
  font-size: 0.85rem;
  color: #8B4F13;
  white-space: nowrap;
  z-index: 10;
  box-shadow: 0 4px 8px rgba(139, 79, 19, 0.1);
  text-align: center;
  pointer-events: none;
  animation: fade-up 0.3s ease;
}

@keyframes fade-up {
  from { opacity: 0; transform: translate(-50%, 10px); }
  to { opacity: 1; transform: translate(-50%, 0); }
}

/* Animation transitions */
.fade-enter-active, .fade-leave-active {
  transition: opacity 1s ease, transform 1s ease;
}

.fade-enter-from, .fade-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

.fade-enter-to, .fade-leave-from {
  opacity: 1;
  transform: scale(1);
}

.fade-in-enter-active {
  transition: opacity 0.8s ease, transform 0.8s ease;
  transition-delay: 0.2s;
}

.fade-in-leave-active {
  transition: opacity 0.6s ease, transform 0.6s ease;
}

.fade-in-enter-from {
  opacity: 0;
  transform: scale(0.95);
}

.fade-in-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

/* Input panel styling */
.input-panel {
  padding: 20px;
  width: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.input-panel form {
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
}

.input-panel h3 {
  font-family: 'Noto Serif SC', serif;
  color: #8B4F13;
  text-align: center;
  margin: 0;
}

.heading-container {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  position: relative;
}

.password-container {
  position: relative;
  width: 80%;
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.password-input {
  width: 100%;
  padding: 12px;
  border: 1px solid rgba(184, 134, 50, 0.3);
  border-radius: 8px;
  background-color: #fffaf5;
  font-family: 'Noto Sans SC', sans-serif;
  font-size: 1rem;
  text-align: center;
  transition: border 0.3s, box-shadow 0.3s;
  margin-bottom: 0;
}

.password-input:focus {
  outline: none;
  border-color: rgba(184, 134, 50, 0.7);
  box-shadow: 0 0 8px rgba(184, 134, 50, 0.2);
}

.help-icon {
  position: relative;
  margin-left: 10px;
  cursor: pointer;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background-color: rgba(184, 134, 50, 0.1);
  border: 1px solid rgba(139, 79, 19, 0.3);
  color: #8B4F13;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 0.8rem;
  font-weight: bold;
  transition: all 0.3s ease;
}

.help-icon:hover {
  background-color: rgba(184, 134, 50, 0.2);
  box-shadow: 0 2px 4px rgba(139, 79, 19, 0.15);
}

.tooltip {
  position: absolute;
  top: calc(100% + 10px);
  left: 50%;
  transform: translateX(-50%);
  width: 220px;
  background-color: #fff9f0;
  border: 1px solid rgba(139, 79, 19, 0.3);
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 0.85rem;
  color: #8B4F13;
  line-height: 1.4;
  z-index: 10;
  box-shadow: 0 4px 8px rgba(139, 79, 19, 0.1);
  text-align: center;
  pointer-events: none;
  animation: fade-in 0.3s ease;
  white-space: normal;
  word-wrap: break-word;
}

.tooltip::before {
  content: '';
  position: absolute;
  top: -6px;
  left: 50%;
  margin-left: -6px;
  width: 12px;
  height: 12px;
  background-color: #fff9f0;
  border-left: 1px solid rgba(139, 79, 19, 0.3);
  border-top: 1px solid rgba(139, 79, 19, 0.3);
  transform: rotate(45deg);
}

@keyframes fade-in {
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
}

.submit-btn {
  padding: 10px 25px;
  background: linear-gradient(135deg, #c69c6d 0%, #8B4F13 100%);
  color: white;
  border: none;
  border-radius: 6px;
  font-family: 'Noto Sans SC', sans-serif;
  font-size: 1rem;
  cursor: pointer;
  transition: all 0.3s ease;
}

.submit-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 8px rgba(139, 79, 19, 0.25);
}

.submit-btn:disabled {
  opacity: 0.7;
  cursor: not-allowed;
  transform: none;
  box-shadow: none;
}

.password-input:disabled {
  background-color: #f5f5f5;
  cursor: not-allowed;
}

.error-message {
  color: #ff3b30;
  font-size: 14px;
  margin: 10px 0;
  text-align: center;
  font-weight: 500;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .page-title {
    font-size: 2.5rem;
    margin-bottom: 1.2rem;
  }
  
  .letter-container {
    max-width: 95%;
  }
  
  .signature-image {
    max-width: 120px;
    bottom: 15px;
    right: 15px;
  }
  
  .input-panel {
    padding: 15px;
    min-height: 350px;
  }
  
  .input-panel form {
    width: 100%;
  }
  
  .heading-container {
    margin-bottom: 15px;
  }
  
  .password-container {
    width: 85%;
  }
  
  .password-input {
    padding: 10px;
    width: 100%;
  }
  
  .help-icon {
    width: 18px;
    height: 18px;
    font-size: 0.75rem;
    margin-left: 8px;
  }
  
  .tooltip {
    width: 200px;
    font-size: 0.8rem;
    padding: 6px 10px;
  }
  
  .submit-btn {
    padding: 8px 16px;
  }
  
  .song-tooltip {
    font-size: 0.8rem;
    padding: 5px 10px;
  }
}

@media (max-width: 480px) {
  .page-title {
    font-size: 2rem;
    margin-bottom: 1rem;
  }
  
  .container {
    padding: 10px;
  }
  
  .letter-container {
    border-radius: 8px;
  }
  
  .signature-image {
    max-width: 100px;
    bottom: 10px;
    right: 10px;
  }
  
  .input-panel {
    padding: 10px;
    min-height: 300px;
  }
  
  .input-panel h3 {
    font-size: 1.2rem;
    margin-bottom: 0;
  }
  
  .heading-container {
    margin-bottom: 12px;
  }
  
  .password-container {
    width: 90%;
  }
  
  .password-input {
    padding: 8px;
    font-size: 0.9rem;
    width: 100%;
  }
  
  .help-icon {
    width: 16px;
    height: 16px;
    font-size: 0.7rem;
    margin-left: 6px;
  }
  
  .tooltip {
    width: 180px;
    font-size: 0.75rem;
    padding: 5px 8px;
  }
  
  .submit-btn {
    padding: 7px 14px;
    font-size: 0.9rem;
  }
  
  .song-tooltip {
    font-size: 0.75rem;
    padding: 4px 8px;
    bottom: 15px;
  }
  
  .close-btn {
    width: 26px;
    height: 26px;
    top: 10px;
    right: 10px;
    font-size: 1.3rem;
  }
}

/* For very large screens */
@media (min-width: 1600px) {
  .page-title {
    font-size: 3.5rem;
    margin-bottom: 2rem;
  }
}

.close-btn {
  position: absolute;
  top: 15px;
  right: 15px;
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background-color: rgba(184, 134, 50, 0.1);
  color: #8B4F13;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 1.5rem;
  cursor: pointer;
  transition: all 0.3s ease;
  z-index: 5;
}

.close-btn:hover {
  background-color: rgba(184, 134, 50, 0.3);
  transform: scale(1.1);
}

.video-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.video-container {
  position: relative;
  width: 80%;
  max-width: 800px;
  aspect-ratio: 16/9;
  background: #000;
  border-radius: 12px;
  overflow: hidden;
}

.video-container iframe {
  width: 100%;
  height: 100%;
}

.close-video-btn {
  position: absolute;
  top: -40px;
  right: 0;
  width: 30px;
  height: 30px;
  background-color: rgba(255, 255, 255, 0.2);
  color: white;
  border-radius: 50%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
  cursor: pointer;
  transition: all 0.3s ease;
}

.close-video-btn:hover {
  background-color: rgba(255, 255, 255, 0.4);
}

@media (max-width: 768px) {
  .video-container {
    width: 90%;
  }
}
</style> 